import useDisplayNotifications from "../../hooks/useDisplayNotifications";
import { DisplayNotification, DisplayNotificationType } from "../../models/DisplayNotification";
import { AiOutlineClose } from "react-icons/ai";
import { MdErrorOutline } from "react-icons/md";
import { CiCircleAlert, CiCircleCheck, CiWarning } from "react-icons/ci";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CiCircleInfo } from "react-icons/ci";
import { Transition } from "@headlessui/react";
import { useEffect, useState } from "react";
import { TextButton } from "../common/Button";
import IconButton from "../common/IconButton";

const DisplayNotificationManager: React.FC = () => {
    const { notifications } = useDisplayNotifications();

    return (
        <div className="fixed top-20 right-0 z-50 space-y-4 max-w-7xl w-full px-4 sm:px-6 md:px-8">
            {notifications.map((notification) => (
                <Notification key={notification.id} notification={notification} />
            ))}
        </div>
    );
}

const Notification: React.FC<{ notification: DisplayNotification }> = ({ notification }) => {
    const { removeNotification } = useDisplayNotifications();
    const [show, setShow] = useState(true);

    const handleClose = () => {
        setShow(false);
        // Wait for animation to complete before removing
        setTimeout(() => {
            if (notification.id) {
                removeNotification(notification.id);
            }
        }, 300);
    }

    return (

        <div className={``}>
            {notification.type === DisplayNotificationType.SUCCESS &&
                <SuccessNotification header={notification.header} message={notification.message} onClose={handleClose} />}
            {notification.type === DisplayNotificationType.ERROR &&
                <ErrorNotification header={notification.header} message={notification.message} onClose={handleClose} onAction={notification.onAction} actionText={notification.actionText} />}
        </div>

    );
}

const SuccessNotification: React.FC<{ header?: string, message: string, onClose: () => void }> = ({ header, message, onClose }) => {
    return (
        <div
            aria-live="assertive"
            className="pointer-events-none"
        >
            <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                    <div className="p-4">
                        <div className="flex items-start">
                            <div className="shrink-0">
                                <CiCircleCheck aria-hidden="true" className="size-6 text-green-400" />
                            </div>
                            <div className="ml-3 w-0 flex-1 pt-0.5">
                                <p className="text-sm font-medium text-gray-900">{header || "Success"}</p>
                                <p className="mt-1 text-sm text-gray-500">{message}</p>
                            </div>
                            <div className="ml-4 flex shrink-0">
                                <IconButton
                                    onClick={onClose}
                                    icon={<AiOutlineClose />}
                                    size="s"
                                />

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const ErrorNotification: React.FC<{ header?: string, message: string, onClose: () => void, onAction?: () => void, actionText?: string }> = ({ header, message, onClose, onAction, actionText }) => {
    return (
        <div
            aria-live="assertive"
            className="pointer-events-none"
        >
            <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                    <div className="p-4">
                        <div className="flex items-start">
                            <div className="shrink-0">
                                <CiCircleAlert aria-hidden="true" className="size-6 text-red-400" />
                            </div>
                            <div className="ml-3 w-0 flex-1 pt-0.5">
                                <p className="text-sm font-medium text-gray-900">{header || "Error"}</p>
                                <p className="mt-1 text-sm text-gray-500">
                                    {message}
                                </p>
                                <div className="mt-3 flex space-x-7 justify-end">
                                    <TextButton
                                        onClick={onClose}
                                        theme="neutral"
                                        size="small"
                                    >
                                        Dismiss
                                    </TextButton>
                                    {onAction &&
                                        <TextButton
                                            onClick={() => {
                                                onAction();
                                                onClose();
                                            }}
                                            theme="primary"
                                            size="small"
                                        >
                                            {actionText}
                                        </TextButton>}
                                </div>
                            </div>
                            <div className="ml-4 flex shrink-0">
                                <IconButton
                                    onClick={onClose}
                                    icon={<AiOutlineClose />}
                                    size="s"
                                />

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DisplayNotificationManager;
